import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BookOpen, GraduationCap, Plane, Home, Scale, ChevronDown, ChevronUp } from 'lucide-react';
import AdmissionsForm from './eligibility/AdmissionsForm';
import CoachingForm from './eligibility/CoachingForm';
import TravelForm from './eligibility/TravelForm';
import AccommodationForm from './eligibility/AccommodationForm';
// import ImmigrationForm from './eligibility/ImmigrationForm'; // Replaced by Hub
import VisaComplianceHub from './immigration/VisaComplianceHub';
import EnglishPrepLab from './eligibility/EnglishPrepLab';
import SEO from './SEO';

type ServiceType = 'admissions' | 'coaching' | 'travel' | 'accommodation' | 'immigration' | 'english-prep';

const EligibilityCheck = () => {
    const [searchParams] = useSearchParams();
    const serviceParam = searchParams.get('service') as ServiceType | null;
    const [activeService, setActiveService] = useState<ServiceType | null>(serviceParam || 'admissions');

    // Refs for scrolling to active element
    const serviceRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

    useEffect(() => {
        if (serviceParam && ['admissions', 'coaching', 'travel', 'accommodation', 'immigration', 'english-prep'].includes(serviceParam)) {
            setActiveService(serviceParam);
        }
    }, [serviceParam]);

    const services = [
        { id: 'admissions', label: 'Admissions Support', icon: GraduationCap, description: 'Eligibility for university admission' },
        { id: 'english-prep', label: 'English Prep Hub', icon: BookOpen, description: 'AI Diagnostic for IELTS/PTE' },
        // { id: 'coaching', label: 'Pre-Arrival Coaching', icon: BookOpen, description: 'Prepare for your life in the UK' },
        { id: 'travel', label: 'Travel & Transport', icon: Plane, description: 'Flight and arrival assistance' },
        { id: 'accommodation', label: 'AI Accommodation Matcher', icon: Home, description: 'Finding your perfect stay' },
        { id: 'immigration', label: 'Immigration Advisory', icon: Scale, description: 'Visa and legal guidance' },
    ];

    const renderForm = (serviceId: string) => {
        switch (serviceId) {
            case 'admissions': return <AdmissionsForm />;
            case 'english-prep': return <EnglishPrepLab />;
            case 'coaching': return <CoachingForm />;
            case 'travel': return <TravelForm />;
            case 'accommodation': return <AccommodationForm />;
            case 'immigration': return <VisaComplianceHub />;
            default: return null;
        }
    };

    const toggleService = (id: ServiceType) => {
        if (activeService === id) {
            setActiveService(null); // Allow toggling off
        } else {
            setActiveService(id);
        }
    };

    const getServiceSEODesc = (id: ServiceType | null) => {
        switch (id) {
            case 'admissions': return "Check your eligibility for international university admissions with our AI-powered matcher.";
            case 'english-prep': return "Take a free AI diagnostic for IELTS/PTE and get a personalized prep roadmap.";
            case 'travel': return "Plan your arrival and travel logistics with our intelligent assistant.";
            case 'accommodation': return "Find safe and verified student accommodation near your chosen university.";
            case 'immigration': return "Get expert visa and legal guidance for your international relocation.";
            default: return "Verify your eligibility for various international education and relocation services.";
        }
    };

    const getServiceSEOTitle = (id: ServiceType | null) => {
        if (!id) return "Eligibility Check";
        const label = services.find(s => s.id === id)?.label || "Eligibility Check";
        return `${label}`;
    };

    return (
        <div className="bg-gray-50 min-h-screen pt-40 pb-20 px-4 md:px-6 transition-all duration-500">
            <SEO
                title={getServiceSEOTitle(activeService)}
                description={getServiceSEODesc(activeService)}
                canonical={`/eligibility${activeService ? `?service=${activeService}` : ''}`}
            />
            <div className="max-w-4xl mx-auto">
                <div className="text-center mb-12 animate-fadeIn">
                    <span className="text-[#009B8F] font-bold tracking-widest text-xs uppercase mb-4 block">Start Your Journey</span>
                    <h1 className="text-3xl md:text-5xl font-serif text-[#011E26] mb-4">Eligibility Check</h1>
                    <p className="text-slate-600 max-w-xl mx-auto text-base md:text-lg font-light">
                        Select a service below to verify your eligibility.
                    </p>
                </div>

                <div className="space-y-4">
                    {services
                        .filter(service => activeService ? service.id === activeService : true)
                        .map((service) => (
                            <div
                                key={service.id}
                                ref={el => serviceRefs.current[service.id] = el}
                                className={`bg-white rounded-xl shadow-md overflow-hidden transition-all duration-500 ease-in-out border ${activeService === service.id ? 'border-[#009B8F] ring-1 ring-[#009B8F]/20' : 'border-transparent hover:shadow-lg'
                                    }`}
                            >
                                {/* Accordion Header */}
                                <button
                                    onClick={() => toggleService(service.id as ServiceType)}
                                    className={`w-full flex items-center justify-between p-6 text-left transition-colors ${activeService === service.id ? 'bg-[#009B8F] text-white' : 'bg-white text-[#011E26] hover:bg-gray-50'
                                        }`}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`p-2 rounded-lg ${activeService === service.id ? 'bg-white/20 text-white' : 'bg-[#F3F4F6] text-[#009B8F]'}`}>
                                            <service.icon size={24} />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-lg">{service.label}</h3>
                                            <p className={`text-xs ${activeService === service.id ? 'text-teal-100' : 'text-slate-500'}`}>{service.description}</p>
                                        </div>
                                    </div>
                                    {activeService === service.id ?
                                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest bg-white/20 px-3 py-1 rounded hover:bg-white/30 transition-colors">
                                            Close <ChevronUp size={16} />
                                        </div>
                                        : <ChevronDown className="text-gray-400" />
                                    }
                                </button>

                                {/* Accordion Content */}
                                <div
                                    className={`transition-all duration-500 ease-in-out ${activeService === service.id ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'
                                        }`}
                                >
                                    {activeService === service.id && (
                                        <div className="p-4 md:p-8 border-t border-gray-100 bg-white">
                                            {renderForm(service.id)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                </div>
            </div>
        </div>
    );
};

export default EligibilityCheck;
